import { useEffect, useCallback, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  headerActions = null,
  contentClassName = '',
  headerClassName = '',
  bodyClassName = '',
}) {
  const titleId = useId();
  const contentRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus-trap: keep Tab/Shift-Tab cycling inside the dialog so
      // keyboard users can't accidentally land on background controls
      // while the modal is open. Skipped for non-Tab keys and when the
      // dialog has no focusable descendants.
      if (e.key !== 'Tab') return;
      const node = contentRef.current;
      if (!node) return;
      const focusable = node.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        e.preventDefault();
        node.focus?.();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Focus management: stash the element that opened the modal, move focus
  // into the dialog on open, and restore focus on close. Falls back to
  // focusing the dialog container when there are no focusable children.
  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const node = contentRef.current;
    if (node) {
      const focusable = node.querySelector(FOCUSABLE_SELECTOR);
      (focusable ?? node).focus?.();
    }
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const container = document.getElementById('modalContainer');
  if (!container) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div
        ref={contentRef}
        className={`${styles.content} ${styles[size]} ${contentClassName}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {title && (
          <div className={`${styles.header} ${headerClassName}`}>
            <h2 id={titleId}>{title}</h2>
            {headerActions && (
              <div className={styles.headerActions}>
                {headerActions}
              </div>
            )}
            <button className={styles.close} onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
        )}
        <div className={`${styles.body} ${bodyClassName}`}>{children}</div>
      </div>
    </div>,
    container,
  );
}
